import { rpx } from 'client/lib/rpx-client';
import { useEffect, useState } from 'preact/hooks';
import { LoadingIndicator } from '@components/loading-indicator';
import { showError } from '@components/app-error';
import { CourseMembersDrawer } from './course-members-drawer';
import { CourseLessonActivityDrawer } from './course-lesson-activity-drawer';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';

type Course = Awaited<ReturnType<typeof rpx.coursesOverview.getCoursesOverview>>['courses'][number];

export function CoursesTab() {
  const [courses, setCourses] = useState<Course[] | undefined>(undefined);
  const [active, setActive] = useState<Course | undefined>(undefined);
  const [lessonsFor, setLessonsFor] = useState<Course | undefined>(undefined);

  async function refresh() {
    try {
      const result = await rpx.coursesOverview.getCoursesOverview();
      setCourses(result.courses);
    } catch (err) {
      showError(err);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  if (!courses) {
    return <LoadingIndicator />;
  }

  return (
    <div class="flex flex-col gap-6">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">Courses</h2>
        <p class="text-sm text-gray-500">
          Per-course view of who's enrolled and how they got there. Internal Mirasee accounts are
          filtered out.
        </p>
      </div>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {courses.map((c) => (
          <CourseCard
            key={c.courseId}
            course={c}
            onOpenMembers={() => setActive(c)}
            onOpenLessons={() => setLessonsFor(c)}
          />
        ))}
      </div>

      {active && (
        <CourseMembersDrawer
          courseId={active.courseId}
          title={active.title}
          onClose={() => setActive(undefined)}
        />
      )}

      {lessonsFor && (
        <CourseLessonActivityDrawer
          courseId={lessonsFor.courseId}
          title={lessonsFor.title}
          onClose={() => setLessonsFor(undefined)}
        />
      )}
    </div>
  );
}

function CourseCard(props: {
  course: Course;
  onOpenMembers: () => void;
  onOpenLessons: () => void;
}) {
  const c = props.course;
  return (
    <section class="rounded-xl border border-gray-200 bg-white p-5">
      <header class="mb-3 flex items-baseline justify-between gap-3">
        <div class="min-w-0">
          <h3 class="truncate text-base font-semibold text-gray-900">{c.title}</h3>
          {c.isBundle && (
            <span class="mt-0.5 inline-block rounded bg-indigo-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-indigo-700">
              Bundle
            </span>
          )}
        </div>
        <div class="flex shrink-0 items-baseline gap-3">
          <button
            type="button"
            class="text-xs font-medium text-indigo-700 hover:underline"
            onClick={props.onOpenMembers}
          >
            View members →
          </button>
          {!c.isBundle && (
            <button
              type="button"
              class="text-xs font-medium text-indigo-700 hover:underline"
              onClick={props.onOpenLessons}
            >
              Lesson activity →
            </button>
          )}
        </div>
      </header>
      <div class="mb-3">
        <div class="text-2xl font-semibold text-gray-900">{c.enrolledTotal} enrolled</div>
      </div>
      <ul class="flex flex-col gap-1 text-sm text-gray-700">
        {c.standalone > 0 && (
          <li>
            <span class="font-medium text-gray-900">{c.standalone}</span> standalone
            <span class="text-gray-500"> · direct purchase of this course</span>
          </li>
        )}
        {c.fromBundle > 0 && (
          <li>
            <span class="font-medium text-gray-900">{c.fromBundle}</span> from cohort bundle
            <span class="text-gray-500"> · enrolled via bundle purchase</span>
          </li>
        )}
        {c.invited > 0 && (
          <li>
            <span class="font-medium text-gray-900">{c.invited}</span> invited
            <span class="text-gray-500"> · bulk-invited or free-access</span>
          </li>
        )}
        {c.orphan > 0 && (
          <li class="text-gray-500">
            <span class="font-medium text-gray-700">{c.orphan}</span> orphan
            <span> · no traceable purchase or invite</span>
          </li>
        )}
      </ul>
      {c.courseId !== AIP_COURSE_ID && c.aipVisitorsLast30d > 0 && (
        <p class="mt-3 border-t border-gray-100 pt-3 text-xs italic text-gray-500">
          {c.aipVisitorsLast30d} AIP {c.aipVisitorsLast30d === 1 ? 'member' : 'members'} visited
          this course's pages in the last 30 days
        </p>
      )}
    </section>
  );
}
